import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { STATUS_LABEL } from '@/lib/vehicle-status';
import type { LiveStatus } from '@/types/api';

const TONE: Record<LiveStatus, { dot: string; chip: string }> = {
  moving: { dot: 'bg-moving', chip: 'bg-moving-soft text-moving' },
  idle: { dot: 'bg-idle', chip: 'bg-idle-soft text-idle' },
  offline: { dot: 'bg-offline', chip: 'bg-offline-soft text-offline' },
  alert: { dot: 'bg-alert', chip: 'bg-alert-soft text-alert' },
};

/**
 * The dot pulses only while something is actually happening — motion and open
 * alerts. A parked fleet should be visually quiet.
 */
export function StatusDot({
  status,
  className,
  size = 8,
}: {
  status: LiveStatus;
  className?: string;
  size?: number;
}) {
  const animated = status === 'moving' || status === 'alert';

  return (
    <span
      className={cn('relative inline-flex shrink-0 items-center justify-center', className)}
      style={{ width: size, height: size }}
      aria-hidden
    >
      {animated && (
        <motion.span
          className={cn('absolute inset-0 rounded-full', TONE[status].dot)}
          initial={{ opacity: 0.55, scale: 1 }}
          animate={{ opacity: 0, scale: 2.6 }}
          transition={{
            duration: status === 'alert' ? 1.1 : 1.8,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />
      )}
      <span className={cn('relative size-full rounded-full', TONE[status].dot)} />
    </span>
  );
}

/** Soft, fully-rounded pill — the reference's badge treatment. */
export function StatusBadge({
  status,
  className,
  showLabel = true,
}: {
  status: LiveStatus;
  className?: string;
  showLabel?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold',
        TONE[status].chip,
        className,
      )}
    >
      <StatusDot status={status} size={6} />
      {showLabel && STATUS_LABEL[status]}
    </span>
  );
}
